import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export const useTrafficLogs = (initialDate = null) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    initialDate || new Date().toISOString().split('T')[0]
  );
  const [chartData, setChartData] = useState([]);

  const fetchLogs = useCallback(async (date) => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await db.trafficLogs.getByDate(user.id, date);

      if (error) throw error;

      setLogs(data || []);

      const hourlyData = Array(24).fill(0);
      (data || []).forEach(log => {
        if (log.hour >= 0 && log.hour < 24) {
          hourlyData[log.hour] += parseFloat(log.data_used_gb);
        }
      });

      setChartData(hourlyData.map((value, hour) => ({
        hour,
        label: `${hour.toString().padStart(2, '0')}:00`,
        value: parseFloat(value.toFixed(4)),
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLogs(selectedDate);
  }, [fetchLogs, selectedDate]);

  const getLogsForRange = async (startDate, endDate) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      setLoading(true);
      const { data, error } = await db.trafficLogs.getByDateRange(
        user.id,
        startDate,
        endDate
      );

      if (error) throw error;

      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const getTotals = useCallback(() => {
    const total = logs.reduce((sum, log) => sum + parseFloat(log.data_used_gb), 0);
    const byType = logs.reduce((acc, log) => {
      acc[log.proxy_type] = (acc[log.proxy_type] || 0) + parseFloat(log.data_used_gb);
      return acc;
    }, {});

    return { total, byType };
  }, [logs]);

  const changeDate = (date) => {
    setSelectedDate(date);
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }

    return dates;
  };

  return {
    logs,
    chartData,
    loading,
    error,
    selectedDate,
    changeDate,
    getLogsForRange,
    getTotals,
    getAvailableDates,
    refresh: () => fetchLogs(selectedDate),
  };
};

export default useTrafficLogs;
